import type { BlogTag } from "@/lib/ghost";

export type BlogTopic = {
  slug: string;
  name: string;
  ghostTags: string[];
  title: string;
  description: string;
  intro: string;
  featureHref: string;
  ctaLabel: string;
};

export const BLOG_TOPICS: BlogTopic[] = [
  {
    slug: "content-creation",
    name: "Content Creation",
    ghostTags: ["content-creation", "social-media-success"],
    title: "Content Creation Guides | Crayo Blog",
    description:
      "Practical content creation guides for short-form video creators: hooks, formats, and AI tools that help you post more without burning out.",
    intro:
      "Learn how short-form creators plan, film, and publish content faster, with hooks, formats, and AI tools that cut editing time.",
    featureHref: "/features/auto-clip",
    ctaLabel: "Try the AI clip generator",
  },
  {
    slug: "youtube-automation",
    name: "YouTube Automation",
    ghostTags: ["youtube-automation"],
    title: "YouTube Automation Guides | Crayo Blog",
    description:
      "Everything you need to launch and run an automated YouTube channel: legal tips, AI tools, and step by step setup guides for beginners.",
    intro:
      "Find out how creators build automated YouTube channels, from legal requirements to the AI tools that handle scripting and editing.",
    featureHref: "/use-cases/faceless-youtube-channels",
    ctaLabel: "Start a faceless YouTube channel",
  },
  {
    slug: "how-to-go-viral",
    name: "How to Go Viral",
    ghostTags: ["how-to-go-viral"],
    title: "How to Go Viral Guides | Crayo Blog",
    description:
      "Tips and tools for going viral across TikTok, Instagram, and YouTube Shorts, plus editor picks creators use to produce content faster.",
    intro:
      "Get proven tactics for going viral on short-form platforms, along with the editing tools creators use to keep up the pace.",
    featureHref: "/features/auto-clip",
    ctaLabel: "Find your next viral clip",
  },
  {
    slug: "going-viral-on-tiktok",
    name: "Going Viral on TikTok",
    ghostTags: ["going-viral-on-tiktok", "viral-tiktok-strategies"],
    title: "Going Viral on TikTok | Crayo Blog",
    description:
      "TikTok growth strategies, trending sounds, and video maker tools to help you get more views and build a following that keeps coming back.",
    intro:
      "Explore TikTok specific growth tactics, trending sound tips, and the tools creators use to turn one clip into consistent views.",
    featureHref: "/use-cases/clippers",
    ctaLabel: "Turn clips into TikTok hits",
  },
  {
    slug: "youtube-posting-tips",
    name: "YouTube Posting Tips",
    ghostTags: ["youtube-posting-tips"],
    title: "YouTube Posting Tips | Crayo Blog",
    description:
      "When and how to post on YouTube, plus editor comparisons that help you choose the right tool before you upload your next video.",
    intro:
      "Learn the posting habits, formats, and editor comparisons that help YouTube creators publish consistently and grow faster.",
    featureHref: "/use-cases/faceless-youtube-channels",
    ctaLabel: "Automate your YouTube posting",
  },
  {
    slug: "instagram-reels",
    name: "Instagram Reels",
    ghostTags: ["instagram-reels", "viral-instagram-reels"],
    title: "Instagram Reels Guides | Crayo Blog",
    description:
      "Instagram Reels length, formatting, hashtags, and viral tactics, with everything you need to reformat and repost video content well.",
    intro:
      "See how to format, hashtag, and repost Instagram Reels the right way, so every upload has the best shot at reaching new viewers.",
    featureHref: "/features/video-crop",
    ctaLabel: "Crop videos for Instagram Reels",
  },
  {
    slug: "content-ideas",
    name: "Content Ideas",
    ghostTags: ["content-ideas"],
    title: "Content Ideas for Creators | Crayo Blog",
    description:
      "Niche specific content idea lists for social media, from real estate to POV formats, built to help creators beat writers block fast.",
    intro:
      "Browse niche specific content idea lists that give short-form creators a fast starting point whenever inspiration runs low.",
    featureHref: "/features/reddit-story",
    ctaLabel: "Generate viral story videos",
  },
  {
    slug: "download-youtube-videos",
    name: "Download YouTube Videos",
    ghostTags: ["download-youtube-videos"],
    title: "Download YouTube Videos Guides | Crayo Blog",
    description:
      "Step by step guides for downloading YouTube and TikTok videos on any device, without the watermark and without paying for software.",
    intro:
      "Find clear, current guides for saving YouTube and TikTok videos to any device, watermark free and without extra software.",
    featureHref: "/features/youtube-downloader",
    ctaLabel: "Download YouTube videos free",
  },
  {
    slug: "voice-generation",
    name: "AI Voice Generation",
    ghostTags: ["voice-generation"],
    title: "AI Voice Generation Guides | Crayo Blog",
    description:
      "AI voiceover tool comparisons and tutorials for creators who need realistic narration without hiring a voice actor or a studio.",
    intro:
      "Compare AI voice generators and text to speech tools, then learn how creators use them to narrate videos in minutes.",
    featureHref: "/features/ai-voiceover",
    ctaLabel: "Generate an AI voiceover",
  },
  {
    slug: "youtube-shorts",
    name: "YouTube Shorts",
    ghostTags: ["youtube-shorts"],
    title: "YouTube Shorts Guides | Crayo Blog",
    description:
      "The best editing apps and workflows for YouTube Shorts, built for creators who want to publish more without slowing down quality.",
    intro:
      "Discover editing apps and workflows built specifically for YouTube Shorts, so you can publish more without cutting corners.",
    featureHref: "/features/auto-clip",
    ctaLabel: "Auto clip your next Short",
  },
  {
    slug: "youtube-marketing-tips",
    name: "YouTube Marketing Tips",
    ghostTags: ["youtube-marketing-tips"],
    title: "YouTube Marketing Tips | Crayo Blog",
    description:
      "YouTube SEO, promotion tools, and marketing tactics that help creators and agencies get more eyes on every video they publish.",
    intro:
      "Get YouTube SEO and promotion tactics that help creators and agencies turn every upload into more views and subscribers.",
    featureHref: "/use-cases/agencies",
    ctaLabel: "Scale content for your agency",
  },
  {
    slug: "video-editing",
    name: "Video Editing",
    ghostTags: ["types-of-video-editing"],
    title: "Video Editing Guides | Crayo Blog",
    description:
      "Video editor comparisons and editing techniques for creators, covering everything from mobile apps to full desktop software.",
    intro:
      "Compare video editing apps and pick up techniques that help you cut, style, and finish videos faster on any device.",
    featureHref: "/features/video-cutter",
    ctaLabel: "Cut and edit videos fast",
  },
  {
    slug: "viral-marketing",
    name: "Viral Marketing",
    ghostTags: ["viral-marketing", "what-is-viral-marketing"],
    title: "Viral Marketing Guides | Crayo Blog",
    description:
      "What viral marketing means and how brands use storytelling, trends, and social campaigns to reach audiences at almost no cost.",
    intro:
      "Understand what viral marketing actually means and how brands use storytelling and trends to reach big audiences cheaply.",
    featureHref: "/use-cases/brainrot-channels",
    ctaLabel: "Explore viral content formats",
  },
  {
    slug: "short-form-content",
    name: "Short-Form Content",
    ghostTags: ["short-form-content"],
    title: "Short-Form Content Guides | Crayo Blog",
    description:
      "Growth tactics, account setup tips, and editor picks for creators building a short-form video presence on TikTok and beyond.",
    intro:
      "Get growth tactics and tool picks built for short-form video, so you can grow a following without a full production team.",
    featureHref: "/features/auto-clip",
    ctaLabel: "Create short-form clips fast",
  },
  {
    slug: "instagram-monetization",
    name: "Instagram Monetization",
    ghostTags: ["how-to-make-money-on-instagram", "instagram-monetization-methods"],
    title: "Instagram Monetization Guides | Crayo Blog",
    description:
      "How to make money on Instagram through shops, affiliate programs, and monetization payouts, explained step by step for creators.",
    intro:
      "Learn the real ways creators earn money on Instagram, from shops and affiliate links to monetization payouts and requirements.",
    featureHref: "/pricing",
    ctaLabel: "See Crayo pricing plans",
  },
  {
    slug: "social-media-monetization",
    name: "Social Media Monetization",
    ghostTags: ["how-to-make-money-on-social-media"],
    title: "Social Media Monetization Tips | Crayo Blog",
    description:
      "Cross platform ways to make money on social media, from creator funds to platform specific programs on TikTok, YouTube, and more.",
    intro:
      "Compare the ways creators earn across platforms, from built in creator funds to the programs worth applying for first.",
    featureHref: "/pricing",
    ctaLabel: "Compare Crayo pricing plans",
  },
  {
    slug: "tiktok-content-creation",
    name: "TikTok Content Creation",
    ghostTags: ["tiktok-content-creation"],
    title: "TikTok Content Creation | Crayo Blog",
    description:
      "TikTok specific content ideas, editing comparisons, and AI tools that help creators post consistently without running out of ideas.",
    intro:
      "Find TikTok specific content ideas and editing comparisons that keep your posting schedule full without extra effort.",
    featureHref: "/features/auto-clip",
    ctaLabel: "Create TikTok clips with AI",
  },
  {
    slug: "faceless-content-creation",
    name: "Faceless Content Creation",
    ghostTags: ["faceless-content-creation"],
    title: "Faceless Content Creation | Crayo Blog",
    description:
      "AI avatar and video editor comparisons for creators who want to build a channel or brand without ever showing their face.",
    intro:
      "See how creators build entire channels without showing their face, using AI avatars and editing tools built for anonymity.",
    featureHref: "/features/face-swap",
    ctaLabel: "Try faceless video tools",
  },
  {
    slug: "video-automation",
    name: "Video Automation Tools",
    ghostTags: ["video-automation", "video-automation-2"],
    title: "Video Automation Tools | Crayo Blog",
    description:
      "AI tools that automate video production, from upscaling and image to video generation to enhancing footage for ecommerce content.",
    intro:
      "Browse the AI tools creators and brands use to automate video production, from upscaling footage to generating clips from images.",
    featureHref: "/features/auto-clip",
    ctaLabel: "Automate your video production",
  },
  {
    slug: "faceless-youtube",
    name: "Faceless YouTube Channels",
    ghostTags: ["faceless-youtube", "faceless-youtube-niches"],
    title: "Faceless YouTube Channels | Crayo Blog",
    description:
      "The best faceless YouTube niches, AI voice tools, and dubbing software for creators who want to earn without appearing on camera.",
    intro:
      "Explore the most profitable faceless YouTube niches and the AI voice and dubbing tools creators use to run them at scale.",
    featureHref: "/use-cases/faceless-youtube-channels",
    ctaLabel: "Launch your faceless channel",
  },
];

const TOPIC_BY_SLUG = new Map(BLOG_TOPICS.map((topic) => [topic.slug, topic]));

const TOPIC_BY_GHOST_TAG = new Map<string, BlogTopic>();
for (const topic of BLOG_TOPICS) {
  for (const tag of topic.ghostTags) TOPIC_BY_GHOST_TAG.set(tag, topic);
}

export const getTopic = (slug: string): BlogTopic | undefined => TOPIC_BY_SLUG.get(slug);

export const topicForTag = (ghostTagSlug: string): BlogTopic | undefined =>
  TOPIC_BY_GHOST_TAG.get(ghostTagSlug);

export const topicForPost = (post: {
  primary_tag: BlogTag | null;
  tags: BlogTag[];
}): BlogTopic | undefined => {
  if (post.primary_tag) {
    const primary = topicForTag(post.primary_tag.slug);
    if (primary) return primary;
  }
  for (const tag of post.tags) {
    const topic = topicForTag(tag.slug);
    if (topic) return topic;
  }
  return undefined;
};

export const topicFilter = (topic: BlogTopic) => `tags:[${topic.ghostTags.join(",")}]`;
